/**
 * ダッシュボード `/`。
 *
 * 「今すぐ対応が要るか」だけを最短で判断させる画面にする。
 * 数字の羅列より上に**アラート**を置き、各行から該当一覧へ直接飛ばす。
 */
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronRight,
  Flag,
  Info,
  LifeBuoy,
  ListChecks,
  Mail,
  Rocket,
  Send,
  Server,
  Skull,
} from 'lucide-react';
import {
  Badge,
  PageHeader,
  Section,
  StatCard,
  StatusDot,
  UsageBar,
  cn,
  formatBytes,
  formatCount,
  formatRelative,
  JobTimeline,
  EmptyState,
} from '@mail-sv/ui';
import { labelOf, toneOf, OPS_PERMISSION } from '@mail-sv/shared';
import { USAGE_METRIC_LABEL, labelFrom } from '../lib/ops-labels.js';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { formatDelta } from '../lib/format.js';
import { useLivePollInterval } from '../lib/live.js';
import { usePermission } from '../stores/auth.js';
import { QueryError, RefetchingDot, StatCardSkeleton } from '../components/states.js';
import { navTo } from '../components/nav-link.js';
import type {
  DashboardAlert,
  DashboardNodeTile,
  DashboardSummary,
  QueueDepth,
  SupportSummary,
} from '../types/api.js';

export function DashboardPage(): React.ReactElement {
  /*
   * 再取得の間隔は**サーバーが決める**（`GET /ops/events` の `pollIntervalSec`）。
   * SSE は「対応していない」と明示的に返る面なので購読は張らないが、
   * 負荷が上がったときに間隔を伸ばす口はサーバー側に 1 つ持たせておく（A151）。
   */
  const pollMs = useLivePollInterval(15_000);
  const query = useQuery({
    queryKey: qk.dashboard(),
    queryFn: () => api.get<DashboardSummary>('/dashboard'),
    refetchInterval: pollMs,
  });

  const d = query.data;

  return (
    <>
      <PageHeader
        title="ダッシュボード"
        description="プラットフォーム全体の稼働状況と、対応が必要な項目。"
        actions={<RefetchingDot active={query.isFetching && !query.isLoading} />}
      />

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-6" />
      ) : null}

      {query.isLoading || !d ? (
        <StatCardSkeleton />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="稼働ノード"
            value={`${formatCount(d.nodes.healthy)} / ${formatCount(d.nodes.total)}`}
            icon={Server}
            tone={d.nodes.unreachable > 0 ? 'destructive' : d.nodes.degraded > 0 ? 'warning' : 'success'}
            hint={
              d.nodes.unreachable > 0
                ? `応答なし ${d.nodes.unreachable} 台`
                : d.nodes.degraded > 0
                  ? `劣化 ${d.nodes.degraded} 台`
                  : 'すべて正常'
            }
          />
          <StatCard
            label="契約組織"
            value={formatCount(d.organizations.total)}
            icon={Building2}
            hint={`前月比 ${formatDelta(d.organizations.deltaMonth)} / 停止中 ${d.organizations.suspended}`}
          />
          <StatCard
            label="メールボックス"
            value={formatCount(d.mailboxes.total)}
            icon={Mail}
            tone={d.mailboxes.overQuota > 0 ? 'warning' : 'default'}
            hint={`容量超過 ${formatCount(d.mailboxes.overQuota)} 件`}
          />
          <StatCard
            label="24h 送信通数"
            value={formatCount(d.mail.sent24h)}
            icon={Send}
            hint={`前日比 ${formatDelta(d.mail.deltaSent)} / 拒否 ${formatCount(d.mail.rejected24h)}`}
          />
        </div>
      )}

      {d ? (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Section title="対応が必要な項目">
              <AlertPanel alerts={d.alerts} />
            </Section>

            <Section
              title="実行中のジョブ"
              actions={
                <Link
                  to="/jobs"
                  search={{ tab: 'provisioning', since: '24h', outboxStatus: 'pending' }}
                  className="text-xs text-primary hover:underline"
                >
                  すべて見る
                </Link>
              }
            >
              {d.runningJobs.length === 0 ? (
                <EmptyState title="実行中のジョブはありません" />
              ) : (
                <div className="rounded-lg border border-border bg-panel-elevated p-4">
                  <JobTimeline
                    steps={d.runningJobs.slice(0, 5).map((j) => ({
                      id: j.id,
                      name: j.kind,
                      status: j.status,
                      startedAt: j.startedAt,
                      finishedAt: j.finishedAt,
                      message: j.organizationName ?? j.subjectId ?? undefined,
                      attempt: j.attempts,
                    }))}
                  />
                </div>
              )}
            </Section>
          </div>

          <div className="space-y-6">
            <Section
              title="ノード健全性"
              actions={
                <Link to="/nodes" search={{}} className="text-xs text-primary hover:underline">
                  一覧へ
                </Link>
              }
            >
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1">
                {d.nodeTiles.map((tile) => (
                  <NodeTile key={tile.id} tile={tile} />
                ))}
                {d.nodeTiles.length === 0 ? <EmptyState title="ノードが登録されていません" /> : null}
              </div>
            </Section>

            <Section title="ストレージ">
              <div className="rounded-lg border border-border bg-card p-4">
                <UsageBar
                  used={d.storage.usedBytes}
                  total={d.storage.capacityBytes}
                  label="全ノード合計"
                  formatValue={(n) => formatBytes(n)}
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  稼働中 {formatCount(d.organizations.active)} 契約 / 支払い遅延{' '}
                  {formatCount(d.organizations.pastDue)} 件
                </p>
              </div>
            </Section>
          </div>
        </div>
      ) : null}

      {d ? <OpsWorkload summary={d} /> : null}

      <SupportQueue />

      <PlatformUsage />

      {d ? (
        <Section title="最近のアクティビティ" className="mt-6">
          <RecentActivity entries={d.recentAudit} />
        </Section>
      ) : null}
    </>
  );
}

/* --------------------------------------------------------------- アラート */

const ALERT_ICON: Record<DashboardAlert['severity'], React.ComponentType<{ className?: string }>> = {
  critical: AlertTriangle,
  warning: AlertTriangle,
  info: Info,
};

function AlertPanel({ alerts }: { alerts: DashboardAlert[] }): React.ReactElement {
  if (alerts.length === 0) {
    return (
      <EmptyState
        icon={CheckCircle2}
        title="対応が必要な項目はありません"
        description="すべてのノード・ジョブ・証明書・請求が正常です。"
      />
    );
  }

  // 重大度順。同じ重大度なら件数の多い方を上に。
  const sorted = [...alerts].sort((a, b) => {
    const rank = { critical: 0, warning: 1, info: 2 } as const;
    return rank[a.severity] - rank[b.severity] || b.count - a.count;
  });

  return (
    <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
      {sorted.map((alert) => {
        const Icon = ALERT_ICON[alert.severity];
        return (
          <Link
            key={alert.kind}
            to={navTo(alert.href)}
            className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-hover"
          >
            <Icon
              className={cn(
                'size-4 shrink-0',
                alert.severity === 'critical' ? 'text-destructive' : alert.severity === 'warning' ? 'text-warning' : 'text-info',
              )}
              aria-hidden
            />
            <span className="min-w-0 flex-1 text-sm text-foreground">{alert.message}</span>
            <Badge variant={alert.severity === 'critical' ? 'destructive' : alert.severity === 'warning' ? 'warning' : 'info'}>
              {formatCount(alert.count)}
            </Badge>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          </Link>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------- ノード健全性タイル */

function NodeTile({ tile }: { tile: DashboardNodeTile }): React.ReactElement {
  const tone = toneOf(tile.displayStatus);
  return (
    <Link
      to="/nodes/$nodeId"
      params={{ nodeId: tile.id }}
      search={{ tab: 'overview', range: '24h' }}
      className="block rounded-lg border border-border bg-card p-3 transition-colors hover:border-primary/50 hover:bg-hover"
    >
      <div className="flex items-center gap-2">
        <StatusDot tone={tone === 'default' ? 'muted' : tone} pulse={tile.displayStatus === 'provisioning'} />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{tile.hostname}</span>
        <span className="text-[11px] text-muted-foreground">{labelOf(tile.displayStatus)}</span>
      </div>
      <div className="mt-2 space-y-1.5">
        <MiniBar label="CPU" ratio={tile.cpuPercent === null ? null : tile.cpuPercent / 100} />
        <MiniBar label="メモリ" ratio={tile.memoryRatio} />
        <MiniBar label="ディスク" ratio={tile.diskRatio} />
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        キュー滞留 <span className="tabular">{formatCount(tile.queueDepth)}</span>
      </p>
    </Link>
  );
}

function MiniBar({ label, ratio }: { label: string; ratio: number | null }): React.ReactElement {
  const pct = ratio === null ? 0 : Math.min(Math.max(ratio, 0), 1);
  const tone = pct >= 0.95 ? 'bg-destructive' : pct >= 0.8 ? 'bg-warning' : 'bg-primary';
  return (
    <div className="flex items-center gap-2">
      <span className="w-12 shrink-0 text-[10px] text-muted-foreground">{label}</span>
      <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full', tone)} style={{ width: `${pct * 100}%` }} />
      </div>
      <span className="w-9 shrink-0 text-right text-[10px] tabular text-muted-foreground">
        {ratio === null ? '—' : `${Math.round(pct * 100)}%`}
      </span>
    </div>
  );
}

/* ----------------------------------------------------- 最近のアクティビティ */

function RecentActivity({ entries }: { entries: DashboardSummary['recentAudit'] }): React.ReactElement {
  if (entries.length === 0) return <EmptyState title="記録がありません" />;
  return (
    <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
      {entries.map((e) => (
        <div key={e.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
          <span className="w-32 shrink-0 text-xs tabular text-muted-foreground">{formatRelative(e.occurredAt)}</span>
          <Badge variant="muted" size="sm">
            {labelOf(e.actorType)}
          </Badge>
          <span className="min-w-0 truncate font-medium">{e.actorLabel}</span>
          <code className="rounded bg-panel px-1.5 py-0.5 font-mono text-xs text-muted-foreground">{e.action}</code>
          {e.targetId ? (
            <span className="min-w-0 truncate text-xs text-muted-foreground">{e.targetType} / {e.targetId}</span>
          ) : null}
          {!e.success ? (
            <Badge variant="destructive" size="sm">
              失敗
            </Badge>
          ) : null}
        </div>
      ))}
      <div className="px-4 py-2">
        <Link to="/audit" search={{ since: '7d' }} className="text-xs text-primary hover:underline">
          監査ログをすべて見る
        </Link>
      </div>
    </div>
  );
}

/* ------------------------------------------------- プラットフォーム使用量 */

/**
 * 全組織横断の使用量集計（A151）。
 *
 * `GET /ops/usage` は `usage_daily` の生の日次行（最大 5000 件）を返すだけで、
 * 集計はしてくれない。**この API はこれまでどこからも呼ばれていなかった**ので、
 * 「先月と比べて容量がどれだけ増えたか」を運営が見る手段が無かった。
 * ここでメトリクスごとに合計して出す（月次の売上・容量の予測に使う）。
 *
 * `usage.read` を持たない運営者には出さない（403 のパネルを並べても意味が無い）。
 */
function PlatformUsage(): React.ReactElement | null {
  const canRead = usePermission(OPS_PERMISSION.usageRead);

  const query = useQuery({
    queryKey: qk.platformUsage(),
    queryFn: () => api.get<PlatformUsageResponse>('/usage'),
    enabled: canRead,
    // 日次の確定値なので頻繁に引き直す意味が無い。
    staleTime: 10 * 60_000,
  });

  if (!canRead) return null;

  const items = query.data?.items ?? [];

  // メトリクスごとに合計する。`aggregation` が `max`（容量のような
  // ストック値）のものを足すと意味の無い数字になるので、そこだけ
  // 「期間内の最大」を採る。
  const totals = new Map<string, { value: number; aggregation: string; finalized: number; total: number }>();
  for (const row of items) {
    const cur = totals.get(row.metric) ?? { value: 0, aggregation: row.aggregation, finalized: 0, total: 0 };
    cur.value = row.aggregation === 'max' ? Math.max(cur.value, row.value) : cur.value + row.value;
    cur.total += 1;
    if (row.finalized) cur.finalized += 1;
    totals.set(row.metric, cur);
  }

  const rows = [...totals.entries()].sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <Section
      title="プラットフォーム使用量"
      description={
        query.data
          ? `${query.data.period.from} 〜 ${query.data.period.to}（全組織の合計。容量のようなストック値は期間内の最大）`
          : '直近 30 日'
      }
      className="mt-6"
    >
      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      {rows.length === 0 && !query.isLoading && !query.isError ? (
        <EmptyState
          title="使用量の記録がありません"
          description="日次の集計は worker の usage キューが作ります。"
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {rows.map(([metric, agg]) => (
            <StatCard
              key={metric}
              // `labelOf()` は使用量メトリクスの語彙を持たない（生値が大文字で出る）。
              label={labelFrom(USAGE_METRIC_LABEL, metric)}
              value={
                // 容量系（`_bytes` で終わるメトリクス）はバイト表記にする。
                metric.endsWith('_bytes') ? formatBytes(agg.value) : formatCount(agg.value)
              }
              hint={
                /*
                 * **「日分」と書かない。** `GET /ops/usage` は
                 * **組織 × 日**の生行を返すので、行数は日数ではない
                 * （105 組織 × 30 日 = 3,150 行）。日数として読ませると
                 * 「30 日の期間なのに 114 日分」という意味不明な表示になる。
                 */
                agg.finalized < agg.total
                  ? `未確定 ${formatCount(agg.total - agg.finalized)} 件を含む（全 ${formatCount(agg.total)} 件の日次記録）`
                  : `${formatCount(agg.total)} 件の日次記録（すべて確定済み）`
              }
            />
          ))}
        </div>
      )}
    </Section>
  );
}

/** `GET /ops/usage` の応答（日次の生行）。集計は画面側で行う。 */
interface PlatformUsageResponse {
  period: { from: string; to: string };
  items: Array<{
    organizationId: string;
    date: string;
    metric: string;
    subjectType: string | null;
    subjectId: string | null;
    value: number;
    aggregation: string;
    finalized: boolean;
  }>;
}


/* ------------------------------------------------------------ 問い合わせ */

/* ----------------------------------------------------------- 運用の滞留 */

/**
 * **「今、誰かが手を動かさないと前に進まないもの」の件数**。
 *
 * アラート帯は「異常」しか出さない（`alerts` は閾値を超えたものだけ）。
 * ロールアウトが 3 本並走している・通報が 12 件未対応・キューに 4,000 件
 * 待っている、はどれも異常ではないが**放置すると異常になる**種類の数字で、
 * これまでダッシュボードのどこにも出ていなかった（API は返していたのに
 * 画面の型に無く、1 度も読まれていなかった）。
 *
 * ジョブの待ち件数だけは `GET /ops/dashboard` に無いので
 * `GET /ops/jobs/queues`（BullMQ の生カウンタ）を別に引く。
 * **`ops.job.read` が無い運営者にはタイルごと出さない**（開けない導線を並べない）。
 */
function OpsWorkload({ summary }: { summary: DashboardSummary }): React.ReactElement {
  const canReadJobs = usePermission(OPS_PERMISSION.jobRead);
  const queues = useQuery({
    queryKey: qk.queues(),
    queryFn: () => api.get<{ items: QueueDepth[] }>('/jobs/queues'),
    enabled: canReadJobs,
    refetchInterval: 30_000,
  });

  /*
   * 「未処理」＝ まだ実行されていないもの。`waiting` と `delayed`（再試行待ち・
   * 予定時刻待ち）を足す。`active` は今まさに走っているので滞留ではない。
   * `dead-letter` キューは**コンシューマを持たない**（`waiting` のまま積まれる
   * のが正常）ため、ここに混ぜると常時「未処理あり」に見える。別タイルで出す。
   */
  const items = queues.data?.items ?? [];
  const pendingJobs = items
    .filter((q) => q.queue !== 'dead-letter')
    .reduce((sum, q) => sum + (q.counts.waiting ?? 0) + (q.counts.delayed ?? 0), 0);
  const deadLetter = items
    .filter((q) => q.queue === 'dead-letter')
    .reduce((sum, q) => sum + (q.counts.waiting ?? 0) + (q.counts.delayed ?? 0) + (q.counts.failed ?? 0), 0);

  return (
    <Section title="運用の滞留" className="mt-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="進行中のロールアウト"
          value={formatCount(summary.rollouts.inProgress)}
          unit="本"
          icon={Rocket}
          tone={summary.rollouts.halted > 0 ? 'destructive' : summary.rollouts.inProgress > 0 ? 'warning' : 'default'}
          hint={summary.rollouts.halted > 0 ? `${summary.rollouts.halted} 本が停止中` : '停止中はありません'}
        />
        <StatCard
          label="未対応の通報"
          value={formatCount(summary.abuse.open)}
          unit="件"
          icon={Flag}
          tone={summary.abuse.open > 0 ? 'warning' : 'success'}
          hint="迷惑行為・スパムの通報"
        />
        {canReadJobs ? (
          <>
            <StatCard
              label="未処理のジョブ"
              value={queues.isLoading ? '—' : formatCount(pendingJobs)}
              unit="件"
              icon={ListChecks}
              tone={pendingJobs > 1000 ? 'warning' : 'default'}
              hint="実行待ち + 再試行待ち"
            />
            <StatCard
              label="デッドレター"
              value={queues.isLoading ? '—' : formatCount(deadLetter)}
              unit="件"
              icon={Skull}
              tone={deadLetter > 0 ? 'destructive' : 'success'}
              hint={deadLetter > 0 ? '再投入するまで誰も実行しません' : '滞留なし'}
            />
          </>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <Link to="/updates" search={{ tab: 'rollouts' }} className="text-primary hover:underline">
          ロールアウトを見る
        </Link>
        <Link to="/security" search={{ tab: 'abuse' }} className="text-primary hover:underline">
          通報を見る
        </Link>
        {canReadJobs ? (
          <>
            <Link
              to="/jobs"
              search={{ tab: 'provisioning', since: '24h', outboxStatus: 'pending' }}
              className="text-primary hover:underline"
            >
              ジョブを見る
            </Link>
            <Link
              to="/jobs"
              search={{ tab: 'dead-letter', since: '24h', outboxStatus: 'pending' }}
              className="text-primary hover:underline"
            >
              デッドレターを見る
            </Link>
          </>
        ) : null}
      </div>

      {queues.isError ? (
        <QueryError error={queues.error} onRetry={() => void queues.refetch()} className="mt-3" />
      ) : null}
    </Section>
  );
}

/**
 * 問い合わせと権利行使要求の滞留（`GET /ops/support/summary`）。
 *
 * **画面が無い間、受け付けたチケットに気付ける手段は通知メールだけ**だった。
 * ダッシュボードに件数を出しておかないと、一次応答の期限（SLA）を過ぎたことに
 * 誰も気付かない。`ops.support.ticket.read` が無い運営者には出さない
 * （出しても開けない導線を並べない）。
 */
function SupportQueue(): React.ReactElement | null {
  const canRead = usePermission(OPS_PERMISSION.supportTicketRead);
  const query = useQuery({
    queryKey: qk.supportSummary(),
    queryFn: () => api.get<SupportSummary>('/support/summary'),
    enabled: canRead,
    refetchInterval: 60_000,
  });

  if (!canRead) return null;
  const d = query.data;

  return (
    <Section
      title="問い合わせ"
      className="mt-6"
      actions={
        <Link
          to="/support"
          search={{ tab: 'tickets', openOnly: true, unreadOnly: true }}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          一覧を開く
        </Link>
      }
    >
      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : d ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard label="未クローズ" value={formatCount(d.tickets.open)} unit="件" icon={LifeBuoy} />
          <StatCard
            label="未割当"
            value={formatCount(d.tickets.unassigned)}
            unit="件"
            tone={d.tickets.unassigned > 0 ? 'warning' : 'default'}
          />
          <StatCard
            label="一次応答の期限超過"
            value={formatCount(d.tickets.overdue)}
            unit="件"
            tone={d.tickets.overdue > 0 ? 'destructive' : 'success'}
          />
          <StatCard
            label="権利行使要求"
            value={formatCount(d.privacyRequests.open)}
            unit="件"
            tone={d.privacyRequests.overdue > 0 ? 'destructive' : 'default'}
            hint={d.privacyRequests.overdue > 0 ? `${d.privacyRequests.overdue} 件が期限超過` : undefined}
          />
        </div>
      ) : (
        <StatCardSkeleton count={4} />
      )}
    </Section>
  );
}
